import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { 
  Calculator, 
  Variable, 
  Shapes, 
  Sigma,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Target,
  Loader2,
  BookOpen,
  Volume2,
  Pause,
  LogIn,
  Crown
} from "lucide-react";
import { formatMathText } from "../utils/mathFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  { id: "arithmetic", name: "Arithmetic", icon: Calculator, color: "icon-arithmetic" },
  { id: "algebra", name: "Algebra", icon: Variable, color: "icon-algebra" },
  { id: "geometry", name: "Geometry", icon: Shapes, color: "icon-geometry" },
  { id: "calculus", name: "Calculus", icon: Sigma, color: "icon-calculus" },
];

const difficulties = [
  { id: "easy", name: "Easy", color: "difficulty-easy" },
  { id: "medium", name: "Medium", color: "difficulty-medium" },
  { id: "hard", name: "Hard", color: "difficulty-hard" },
];

export default function Practice() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [selectedTopic, setSelectedTopic] = useState(topicId || "arithmetic");
  const [difficulty, setDifficulty] = useState("medium");
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState({});
  
  // Audio state
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const audioRef = useRef(null);

  // Subscription state
  const [limitReached, setLimitReached] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error("Please sign in to use Practice Mode");
      navigate("/auth", { state: { from: { pathname: `/practice/${topicId || ''}` } } });
    }
  }, [isAuthenticated, authLoading, navigate, topicId]);

  // Fetch usage info
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUsage = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/usage`);
        setUsageInfo(res.data);
        setLimitReached(!res.data.practice.allowed);
      } catch { /* ignore */ }
    };
    fetchUsage();
  }, [isAuthenticated, questions.length]);

  // Update selected topic when URL param changes
  useEffect(() => {
    if (topicId && topics.find(t => t.id === topicId)) {
      setSelectedTopic(topicId);
    }
  }, [topicId]);

  const generateQuestions = async () => {
    setLoading(true);
    setQuestions([]);
    setExpandedAnswers({});

    try {
      const response = await axios.post(`${API}/practice/generate`, {
        topic: selectedTopic,
        difficulty: difficulty,
        count: 3
      });

      setQuestions(response.data.questions || []);
      toast.success("Questions generated successfully!");
    } catch (error) {
      console.error("Error generating questions:", error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "Daily practice limit reached.");
        setLimitReached(true);
      } else {
        toast.error("Failed to generate questions. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAnswer = (questionId) => {
    setExpandedAnswers(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Audio playback function
  const playAudio = async (questionId, text) => {
    if (playingAudioId === questionId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingAudioId(questionId);

    try {
      const response = await axios.post(
        `${API}/tts`,
        { text, voice: "nova" },
        { responseType: "blob" }
      );

      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAudioId(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        toast.error("Failed to play audio");
        setPlayingAudioId(null);
      };

      await audio.play();
      setPlayingAudioId(questionId);
    } catch (error) {
      console.error("TTS error:", error);
      if (error.response?.status === 403) {
        toast.error("Text-to-Speech requires a Pro or Premium plan.", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Failed to generate audio");
      }
    } finally {
      setLoadingAudioId(null);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const currentTopicInfo = topics.find(t => t.id === selectedTopic);
  const TopicIcon = currentTopicInfo?.icon || Calculator;

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 dark:border-slate-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold font-['Outfit'] mb-2">
              Sign In Required
            </h2>
            <p className="text-muted-foreground mb-6">
              Please create an account or sign in to access Practice Mode.
            </p>
            <Link to="/auth" state={{ from: { pathname: `/practice/${topicId || ''}` } }}>
              <Button className="rounded-full px-8">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In to Continue
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-transition py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-xl ${currentTopicInfo?.color || 'bg-primary/10'} flex items-center justify-center`}>
              <TopicIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']" data-testid="practice-title">
                Practice Mode
              </h1>
              <p className="text-muted-foreground">
                Generate and solve practice problems
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <Card className="mb-8 border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            {/* Limit banner */}
            {limitReached && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="practice-limit-banner">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <Crown className="w-4 h-4 shrink-0" />
                  <span>Daily practice limit reached. Upgrade for unlimited.</span>
                </div>
                <Link to="/pricing">
                  <Button size="sm" className="rounded-full text-xs shrink-0" data-testid="practice-upgrade-btn">Upgrade</Button>
                </Link>
              </div>
            )}
            {/* Usage counter */}
            {usageInfo?.practice && !limitReached && usageInfo.practice.remaining > 0 && usageInfo.practice.remaining !== -1 && (
              <div className="mb-3 text-xs text-muted-foreground text-center" data-testid="practice-usage-counter">
                {usageInfo.practice.remaining} practice generation{usageInfo.practice.remaining !== 1 ? 's' : ''} remaining today
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              {/* Topic Selector */}
              <div>
                <label className="text-sm font-medium mb-2 block">Topic</label>
                <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                  <SelectTrigger data-testid="topic-select">
                    <SelectValue placeholder="Select topic" />
                  </SelectTrigger>
                  <SelectContent data-testid="topic-select-content">
                    {topics.map((topic) => {
                      const Icon = topic.icon;
                      return (
                        <SelectItem 
                          key={topic.id} 
                          value={topic.id}
                          data-testid={`topic-${topic.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {topic.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Difficulty Selector */}
              <div>
                <label className="text-sm font-medium mb-2 block">Difficulty</label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger data-testid="difficulty-select">
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent data-testid="difficulty-select-content">
                    {difficulties.map((diff) => (
                      <SelectItem 
                        key={diff.id} 
                        value={diff.id}
                        data-testid={`difficulty-${diff.id}`}
                      >
                        {diff.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Generate Button */}
              <div className="flex items-end">
                <Button 
                  onClick={generateQuestions}
                  disabled={loading || limitReached}
                  className="w-full rounded-xl"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Generate Questions
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        {questions.length > 0 ? (
          <div className="space-y-6" data-testid="questions-list">
            {questions.map((question, index) => (
              <Card 
                key={question.id || index} 
                className="border-slate-200 dark:border-slate-800 overflow-hidden"
                data-testid={`question-card-${index}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                        <span className="text-sm font-bold text-primary">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          difficulty === 'easy' ? 'difficulty-easy' :
                          difficulty === 'medium' ? 'difficulty-medium' : 'difficulty-hard'
                        }`}>
                          {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Question */}
                  <div className="mb-4">
                    <h3 className="text-lg font-medium font-['Outfit'] leading-relaxed">
                      {formatMathText(question.question)}
                    </h3>
                  </div>

                  {/* Answer Toggle */}
                  <Button
                    variant="outline"
                    onClick={() => toggleAnswer(question.id || index)}
                    className="w-full rounded-xl justify-between"
                    data-testid={`toggle-answer-${index}`}
                  >
                    <span className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      {expandedAnswers[question.id || index] ? "Hide Solution" : "Show Solution"}
                    </span>
                    {expandedAnswers[question.id || index] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>

                  {/* Answer & Explanation */}
                  {expandedAnswers[question.id || index] && (
                    <div className="mt-4 space-y-4" data-testid={`answer-${index}`}>
                      <Separator />
                      
                      {/* Answer */}
                      <div className="bg-secondary/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-semibold">Answer</span>
                        </div>
                        <p className="font-mono text-lg">{formatMathText(question.answer)}</p>
                      </div>

                      {/* Explanation */}
                      <div className="bg-muted rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-primary" />
                            <span className="font-semibold">Step-by-Step Solution</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => playAudio(question.id || index, `Question: ${question.question}. Answer: ${question.answer}. Explanation: ${question.explanation}`)}
                            disabled={loadingAudioId === (question.id || index)}
                            className="h-8 px-3 text-xs rounded-full"
                            data-testid={`audio-btn-${index}`}
                          >
                            {loadingAudioId === (question.id || index) ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Loading...
                              </>
                            ) : playingAudioId === (question.id || index) ? (
                              <>
                                <Pause className="w-3 h-3 mr-1" />
                                Stop
                              </>
                            ) : (
                              <>
                                <Volume2 className="w-3 h-3 mr-1" />
                                Listen
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed math-content">
                          {formatMathText(question.explanation)}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Generate More */}
            <div className="text-center">
              <Button
                variant="outline"
                onClick={generateQuestions}
                disabled={loading}
                className="rounded-full"
                data-testid="generate-more-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Generate More Questions
              </Button>
            </div>
          </div>
        ) : (
          // Empty State
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold font-['Outfit'] mb-2">
                Ready to Practice?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Select a topic and difficulty level, then click "Generate Questions" 
                to create practice problems with step-by-step solutions.
              </p>
              <Button 
                onClick={generateQuestions}
                disabled={loading}
                className="rounded-full"
                data-testid="start-practice-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Topic Links */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold font-['Outfit'] mb-4">
            Quick Practice by Topic
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="topic-quick-links">
            {topics.map((topic) => {
              const Icon = topic.icon;
              return (
                <Link 
                  key={topic.id}
                  to={`/practice/${topic.id}`}
                  onClick={() => setSelectedTopic(topic.id)}
                  data-testid={`quick-link-${topic.id}`}
                >
                  <Card className="topic-card border-slate-200 dark:border-slate-800 hover:border-primary/50 cursor-pointer">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${topic.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="font-medium">{topic.name}</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
